import React, { useEffect, useState } from "react";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faTelegramPlane } from "@fortawesome/free-brands-svg-icons";
import { faExternalLinkAlt, faBell, faBellSlash } from "@fortawesome/free-solid-svg-icons";
import { useGetTelegramStatusQuery } from "../../services/rtkApi/messengerApi";
import { useCreateTelegramLinkMutation } from "../../services/rtkApi/messengerReachApi";
import styles from "./TelegramSettings.module.css";

const TELEGRAM_BOT_URL = "https://t.me/n450s_bot";
// link tokens live 10 minutes on the messenger; refresh a little before that
const LINK_REFRESH_MS = 8 * 60 * 1000;
// after a tap, poll the status for a while so the card flips to "Connected" by itself
const LINK_POLL_MS = 4000;
const LINK_POLL_FOR_MS = 3 * 60 * 1000;

// one-tap connect: a t.me deep link carrying a one-time token, so tapping Start in
// Telegram links this account. no code to copy, no second sign-in inside Telegram.
// falls back to the plain bot link until the personal link has loaded.
const ConnectTelegramButton: React.FC<{ onOpened: () => void }> = ({ onOpened }) => {
  const [createLink] = useCreateTelegramLinkMutation();
  const [url, setUrl] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    const load = async () => {
      try {
        const res = await createLink().unwrap();
        if (!cancelled && res.success && res.data?.url) setUrl(res.data.url);
      } catch {
        // keep the plain bot link
      }
    };
    void load();
    const timer = setInterval(load, LINK_REFRESH_MS);
    return () => {
      cancelled = true;
      clearInterval(timer);
    };
  }, [createLink]);

  return (
    <>
      <a
        href={url || TELEGRAM_BOT_URL}
        target="_blank"
        rel="noopener noreferrer"
        className={styles.connectLink}
        onClick={onOpened}
      >
        <button className={styles.connectButton} type="button">
          <FontAwesomeIcon icon={faTelegramPlane} /> Connect to Telegram
        </button>
      </a>
      {url && (
        <p className={styles.mutedHint}>
          Opens Telegram. Tap Start there and you're connected.
        </p>
      )}
    </>
  );
};

// format date string to readable format
function formatDate(dateString: string | null | undefined): string {
  if (!dateString) return "Unknown";
  const date = new Date(dateString);
  return date.toLocaleDateString("en-US", {
    year: "numeric",
    month: "short",
    day: "numeric",
  });
}

const TelegramSettings: React.FC = () => {
  const [pollUntil, setPollUntil] = useState(0);
  const polling = pollUntil > 0;
  const { data, isLoading, isError } = useGetTelegramStatusQuery(undefined, {
    pollingInterval: polling ? LINK_POLL_MS : 0,
  });
  const connected = !!data?.data?.connected;

  // stop polling once linked, or when the window runs out
  useEffect(() => {
    if (!polling) return;
    if (connected) {
      setPollUntil(0);
      return;
    }
    const t = setTimeout(() => setPollUntil(0), Math.max(pollUntil - Date.now(), 0));
    return () => clearTimeout(t);
  }, [polling, pollUntil, connected]);

  const startPolling = () => setPollUntil(Date.now() + LINK_POLL_FOR_MS);

  // loading state
  if (isLoading) {
    return (
      <div className={styles.container}>
        <div className={styles.title}>
          <FontAwesomeIcon icon={faTelegramPlane} className={styles.titleIcon} />
          Telegram Notifications
        </div>
        <p className={styles.loadingText}>Loading...</p>
      </div>
    );
  }

  // error state
  if (isError || !data?.success) {
    return (
      <div className={styles.container}>
        <div className={styles.title}>
          <FontAwesomeIcon icon={faTelegramPlane} className={styles.titleIcon} />
          Telegram Notifications
        </div>
        <p className={styles.description}>
          Connect your Telegram account to receive notifications about AOTD and other updates.
        </p>
        <div className={styles.buttonContainer}>
          <ConnectTelegramButton onOpened={startPolling} />
        </div>
      </div>
    );
  }

  const telegramData = data.data;

  // not connected state
  if (!telegramData?.connected) {
    return (
      <div className={styles.container}>
        <div className={styles.title}>
          <FontAwesomeIcon icon={faTelegramPlane} className={styles.titleIcon} />
          Telegram Notifications
        </div>
        <p className={styles.description}>
          Connect your Telegram account to receive notifications about AOTD and other updates.
          You'll get a heads up before Art of the Day drops!
        </p>
        <div className={styles.buttonContainer}>
          <ConnectTelegramButton onOpened={startPolling} />
        </div>
      </div>
    );
  }

  // connected state
  return (
    <div className={styles.container}>
      <div className={styles.title}>
        <FontAwesomeIcon icon={faTelegramPlane} className={styles.titleIcon} />
        Telegram Notifications
      </div>

      <div className={styles.connectedInfo}>
        <div className={styles.statusRow}>
          <span className={styles.infoLabel}>Status:</span>
          <span className={styles.statusConnected}>Connected</span>
          {telegramData.globalMute ? (
            <span className={`${styles.muteStatus} ${styles.mutedBadge}`}>
              <FontAwesomeIcon icon={faBellSlash} /> Muted
            </span>
          ) : (
            <span className={`${styles.muteStatus} ${styles.activeBadge}`}>
              <FontAwesomeIcon icon={faBell} /> Active
            </span>
          )}
        </div>

        {telegramData.telegramUsername && (
          <div className={styles.statusRow}>
            <span className={styles.infoLabel}>Telegram:</span>
            <span className={styles.telegramUsername}>
              @{telegramData.telegramUsername}
            </span>
          </div>
        )}

        {!telegramData.telegramUsername && telegramData.telegramFirstName && (
          <div className={styles.statusRow}>
            <span className={styles.infoLabel}>Name:</span>
            <span className={styles.infoValue}>{telegramData.telegramFirstName}</span>
          </div>
        )}

        <div className={styles.statusRow}>
          <span className={styles.infoLabel}>Connected since:</span>
          <span className={styles.infoValue}>{formatDate(telegramData.connectedAt)}</span>
        </div>
      </div>

      <a
        href={TELEGRAM_BOT_URL}
        target="_blank"
        rel="noopener noreferrer"
        className={styles.settingsLink}
      >
        <FontAwesomeIcon icon={faExternalLinkAlt} />
        Manage settings in Telegram
      </a>
    </div>
  );
};

export default TelegramSettings;
